import { trpc } from '@tencent/tpro_rmonitor_login_server_login_auth';
import { ENV } from '@/infrastructure/env-config';

import IUserInfo = trpc.rmonitor.login_server.IUserInfo;

export interface Token {
  saasToken: string;
  uin: string;
  ownerUin: string;
  buglyToken: string;
}

export interface AuthInfo {
  isLoggedIn: boolean;
  token?: string;
  saasToken?: string;
  userInfo?: IUserInfo | null;
  loginType?: LoginType;
  // 上次登录的uin
  lastLoginUin?: string;
}

export const BUGLY_LOGIN_STORAGE_KEY = 'bugly_user_auth_info';

export const BUGLY_LAST_LOGIN = 'bugly_last_login';

export const TDS_TOKEN = 'tds_token';

export const TDS_SASS_UIN = 'tds_sass_uin';

export const TDS_SASS_TOKEN = 'tds_sass_token';

export const TDS_USER_ID = 'tds_uid';

export const TDS_USER_NAME = 'tds_username';

export enum LoginType {
  IOA = 'ioa',
  TencentCloud = 'tencent_cloud',
  DjiOauth = 'dji_oauth'
}

export enum TencentCloudLoginType {
  Wechat = 'wechat',
  QQ = 'qq'
}

export enum SubSystemType {
  SubAccount = 'SubAccount',
}

export type TencentCloudAccountOwnerInfo = {
  account: string;
  checkStatus: string | null;
  displayName: string;
  email: string;
  emailFlag: number;
  isDefaultOwner: boolean;
  phone: string;
  phoneFlag: number;
  subSystemType: SubSystemType;
  subUserName: string;
  uin: string;
  userName: string;
  extAttr: {
    needResetStoken: boolean;
    needResetToken: boolean;
  }
};


const randomColor = (userName: string) => {
  let color = '';
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < userName.length; i++) {
    color += parseInt(String(userName[i].charCodeAt(0)), 10).toString(16);
  }

  return `#${color.slice(1, 4)}`;
};

/**
 * 根据用户名生成随机背景的头像
 * 正方形
 * @param userName
 * @param size
 */
export function genAvatar(userName: string, size = 48): string {
  const color = randomColor(userName);
  const center = size / 2;

  const firstChar = userName.substring(0, 1);
  const cvs = document.createElement('canvas');
  cvs.setAttribute('width', `${size}`);
  cvs.setAttribute('height', `${size}`);
  const context = cvs.getContext('2d');

  if (context) {
    context.fillStyle = color;
    context.fillRect(0, 0, size, size);
    context.fillStyle = '#fff';
    context.font = `${size * 0.5}px TencentSans`;
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillText(firstChar, center, center);
  }

  return cvs.toDataURL('image/jpeg', 1);
};


export const genCookieName = (name: string) => (ENV.isProd || ENV.isEu ? name : `test-${name}`);

export enum BuglyEnv {
  Dev = 'dev',
  Test = 'test',
  Prod = 'prod'
}

const baseDomain = ENV.isGlobal ? 'tencent' : 'qq';

const TDS_QQ_PASSPORT_HOST: Record<BuglyEnv, string> = {
  // 本地调试用的域名
  [BuglyEnv.Dev]: 'https://t.tds.qq.com',
  [BuglyEnv.Test]: 'https://t.tds.qq.com',
  [BuglyEnv.Prod]: 'https://tds.qq.com',
};

const TDS_TENCENT_PASSPORT_HOST: Record<BuglyEnv, string> = {
  // 本地调试用的域名
  [BuglyEnv.Dev]: 'https://t.tds.tencent.com',
  [BuglyEnv.Test]: 'https://t.tds.tencent.com',
  [BuglyEnv.Prod]: 'https://tds.tencent.com',
};

const EU_PASSPORT_HOST: Record<BuglyEnv, string> = {
  [BuglyEnv.Dev]: 'https://bugly.zymix.tds.qq.com',
  [BuglyEnv.Test]: 'https://bugly.zymix.tds.qq.com',
  [BuglyEnv.Prod]: 'https://qualix.frontarc.ai',
};

export interface TdsPassportConfig {
  host: Record<BuglyEnv, string>;
  path: string;
  rootDomain: string | Record<BuglyEnv, string>;
  appName: string;
  useMailLogin: boolean;
}

/**
 * TDS Passport 参数显式配置。
 * EU 的 path/appName 复用 SGP 邮箱登录链路，host 使用同源登录域名，cookie root domain 按环境区分。
 */
export const TDS_PASSPORT_CONFIG: Record<string, TdsPassportConfig> = {
  pro: {
    host: TDS_QQ_PASSPORT_HOST,
    path: '/login',
    rootDomain: '.tds.qq.com',
    appName: 'bugly',
    useMailLogin: false,
  },
  sgp: {
    host: TDS_TENCENT_PASSPORT_HOST,
    path: '/login_sgp',
    rootDomain: '.tds.tencent.com',
    appName: 'buglySgp',
    useMailLogin: true,
  },
  eu: {
    host: EU_PASSPORT_HOST,
    path: '/login_sgp',
    rootDomain: {
      [BuglyEnv.Dev]: 'tds.qq.com',
      [BuglyEnv.Test]: 'tds.qq.com',
      [BuglyEnv.Prod]: 'frontarc.ai',
    },
    appName: 'buglySgp',
    useMailLogin: true,
  },
};

export const getTdsPassportConfig = (app = ENV.currentAppVersion || 'pro') => (
  TDS_PASSPORT_CONFIG[app] ?? TDS_PASSPORT_CONFIG.pro
);

export const getTdsPassportRootDomain = (config: TdsPassportConfig, env = ENV.buglyEnv as BuglyEnv) => {
  if (typeof config.rootDomain === 'string') return config.rootDomain;

  return config.rootDomain[env] ?? config.rootDomain[BuglyEnv.Test];
};

export const TDS_PASSPORT_PATH = getTdsPassportConfig().path;

export const TDS_PASSPORT_HOST: Record<BuglyEnv, string> = {
  // 保留历史导出，兼容 pro/sgp 现有实现
  [BuglyEnv.Dev]: `https://t.tds.${baseDomain}.com`,
  [BuglyEnv.Test]: `https://t.tds.${baseDomain}.com`,
  [BuglyEnv.Prod]: `https://tds.${baseDomain}.com`,
};
